import React, { useState } from 'react';

// DEPENDENCIES
import { compose } from 'redux';
import { connect } from 'react-redux';
import { useFormikContext } from 'formik';

// HELPERS
import { withRouter } from 'helpers/withRouter';

// GLOBAL VARIABLES
import { INVENTORY_FILE_TYPE_IDS } from 'globals.js';

// CORE COMPONENTS
import Form from 'core/form/Form';

// GLOBAL PARTS
import Sidebar from 'parts/sidebar/Sidebar';

// LOCAL TOOLS
import VINStock from './tools/VINStock';
import Attachment from 'routers/interface/tools/attachment/Attachment';

// LOCAL VARIABLES
import {
  EMPLOYEE_OPTION_KEYS,
  STATES_KEYS,
  LOOKUP_PARAMS,
  WARRANTY_OPTION_KEYS,
  VEHICLE_MAKE_OPTION_KEYS
} from '../../variables';
import { SPARE_KEY_STATUS } from './options';

// MAIN COMPONENT
const RetailTrade = compose(
  withRouter,
  connect(({ lookups }) => ({ lookups }))
)(({
  initialValues,
  handleCancel,
  stockNum,
  index,
  handleSubmit,
  // REDUX
  lookups
}) => {

    // RENDER
  return (
    <Sidebar.Form
      initialValues={initialValues}
      onSubmit={handleSubmit}
      buttons={{
        cancel: {
          onClick: handleCancel
        },
        submit: {
          variant: 'success',
          label: 'Review In Deal'
        }
      }}
    >
      <RetailTradeForm stockNum={stockNum} index={index} lookups={lookups} />
    </Sidebar.Form>
  );
});

export default RetailTrade;

function RetailTradeForm({
  stockNum,
  index,
  lookups: {
    exteriorColors,
    interiorColors
  }
}) {

  // CONTEXT
  const { values } = useFormikContext() || {};

  // STATE
  const [ possession, setPossessiony ] = useState(parseInt(values?.aryTrade?.[index]?.title_in_possession));
  const [ titleState, setTitleState ] = useState(values?.aryTrade?.[index]?.title_state_code || '');
  const [ needToPay, setNeedToPay ] = useState(parseInt(values?.aryTrade?.[index]?.need_to_pay_off) || 0);

  const tradeStockNum = values.aryTrade?.[index]?.inventory?.stock_num;
  const tradeVinNum = values.aryTrade?.[index]?.inventory?.vin_num;
  const dailySalesInventoryId = values.aryTrade?.[index]?.inventory?.daily_sales_inventory_id;
  const uploadParams = { stockNum: tradeStockNum, vinNum: tradeVinNum, dailySalesInventoryId };
  const noVinStock = !tradeStockNum || !tradeVinNum;
  const vehicle = values.aryTrade?.[index]?.inventory;

    // RENDER
  return (
    <Form.Body vertical fullWidth>
      <Form.PlainText label="Sold Stock #" value={stockNum} />
      <VINStock index={index} required />
      <Form.Control label="Year" name={`aryTrade[${index}].inventory.vehicle_year`} schema="year" required />
      <Form.Select
        name={`aryTrade[${index}].inventory.make_id`}
        label="Make"
        lookup="vehicleMakes"
        optionKeys={VEHICLE_MAKE_OPTION_KEYS}
        useBlank
        required
      />
      <Form.Control name={`aryTrade[${index}].inventory.model`} label="Model" required />
      <Form.Control name={`aryTrade[${index}].inventory.model_code`} label="Model Code" required />
      <Form.Control name={`aryTrade[${index}].inventory.vehicle_trim`} label="Trim" required />
      <Form.Control name={`aryTrade[${index}].inventory.body_style`} label="Body style" required />
      <Form.Control name={`aryTrade[${index}].inventory.mileage`} label="Mileage" schema="miles" required />
      <Attachment.Upload
        label="Photo Proof of Mileage"
        name={`aryTrade[${index}].inventory.documentAttachment.mileage_photo`}
        uploadType="inventory"
        documentType={INVENTORY_FILE_TYPE_IDS.MILEAGE_PHOTO_USED}
        docrName={`aryTrade[${index}].inventory.docrAttachmentRequest`}
        requestedFor="Inventory"
        uploadParams={uploadParams}
        noVinStock={noVinStock}
        vehicle={vehicle}
      />
      <Form.Select
        allowCustom
        name={`aryTrade[${index}].inventory.ext_color`}
        label="Exterior"
        options={exteriorColors}
        required
        key={exteriorColors?.length}
      />
      <Form.Select
        allowCustom
        name={`aryTrade[${index}].inventory.int_color`}
        label="Interior"
        options={interiorColors}
        key={interiorColors?.length}
      />
      <Form.Control name={`aryTrade[${index}].inventory.engine`} label="Engine" />
      <Form.Control name={`aryTrade[${index}].inventory.engine_type`} label="Engine Type" />
      <Form.Control name={`aryTrade[${index}].inventory.transmission_name`} label="Transmission" required />
      <Form.Control name={`aryTrade[${index}].inventory.drivetrain_name`} label="Drivetrain" required />
      <Form.Select
        name={`aryTrade[${index}].inventory.spare_key_status`}
        label="Spare Key Status"
        options={SPARE_KEY_STATUS}
        useBlank
        required
      />
      <Form.Select
        name={`aryTrade[${index}].inventory.warranty_type`}
        label="Warranty type"
        lookup="warrantyTypes"
        optionKeys={WARRANTY_OPTION_KEYS}
        useBlank
        required
      />
      <Form.Control label="ACV" name={`aryTrade[${index}].acv`} schema="dollars" more={0} required />
      <Form.Select
        name={`aryTrade[${index}].inventory.appraised_by_user_id`}
        label="Appraised by"
        lookup="salesManagers"
        lookupParams={LOOKUP_PARAMS.salesManager}
        optionKeys={EMPLOYEE_OPTION_KEYS}
        useBlank
        required
      />
      <Form.Control
        label="Trade in Allowance"
        name={`aryTrade[${index}].trade_in_allowance`}
        schema="dollars"
        more={0}
        required
      />
      <Attachment.Upload
        label="Trade Appraisal"
        name={`aryTrade[${index}].inventory.documentAttachment.trade_appraisal`}
        uploadType="inventory"
        documentType={INVENTORY_FILE_TYPE_IDS.TRADE_APPRAISAL_USED}
        docrName={`aryTrade[${index}].inventory.docrAttachmentRequest`}
        requestedFor="Inventory"
        uploadParams={uploadParams}
        noVinStock={noVinStock}
        vehicle={vehicle}
      />
      <Form.YesNo
        label="Trade Inspection Completed by Service?"
        name={`aryTrade[${index}].trade_inspection_completed`}
        required
      />
      <Attachment.Upload
        label="Trade Inspection"
        name={`aryTrade[${index}].inventory.documentAttachment.trade_inspection`}
        uploadType="inventory"
        documentType={INVENTORY_FILE_TYPE_IDS.TRADE_INSPECTION}
        requestedFor="Inventory"
        uploadParams={uploadParams}
        noVinStock={noVinStock}
        vehicle={vehicle}
        useTempDocUpr={false}
        useDocUpr={false}
      />
      <Form.YesNo
        label="Need To Payoff?"
        name={`aryTrade[${index}].need_to_pay_off`}
        onChange={e => setNeedToPay(parseInt(e.target.value))}
        required
      />
      {needToPay && (
        <Form.Body.Vertical className="m-0">
          <Form.Control label="Bank To Payoff" name={`aryTrade[${index}].bank_to_pay_off`} required />
          <Form.Control label="Loan Account #" name={`aryTrade[${index}].loan_account_number`} />
          <Form.Control label="Phone" name={`aryTrade[${index}].phone_number`} schema="tel" />
          <Form.Control label="Contact" name={`aryTrade[${index}].contact`} />
          <Form.Control label="Payoff Amount" name={`aryTrade[${index}].pay_off_amount`} schema="dollars" required />
          <Form.Date label="Payoff Good Until" name={`aryTrade[${index}].good_until`} text='Note: Payoff Must Be Greater Than 30 Days' required />
          <Form.Control label="Per Diem" name={`aryTrade[${index}].per_diem`} schema="dollars" />
        </Form.Body.Vertical>
      )}
      <Form.Select
        name={`aryTrade[${index}].title_state_code`}
        label="Trade Title State"
        lookup="states"
        optionKeys={STATES_KEYS}
        onSelect={value => setTitleState(value)}
        useBlank
        required
      />
      <Form.YesNo
        name={`aryTrade[${index}].title_in_possession`}
        label="TITLE IN POSSESSION?"
        onChange={e => setPossessiony(parseInt(e.target.value))}
        disabled={needToPay && !['NY', 'MD'].includes(titleState)}
      />
      <Attachment.Upload
        label="Title in Possession"
        name={`aryTrade[${index}].inventory.documentAttachment.trade_title`}
        uploadType="inventory"
        documentType={INVENTORY_FILE_TYPE_IDS.TRADE_POSESSION}
        docrName={`aryTrade[${index}].inventory.docrAttachmentRequest`}
        requestedFor="Inventory"
        uploadParams={uploadParams}
        vehicle={vehicle}
      />
      {possession === 0 && (
        <Form.Date label="Title Available On" name={`aryTrade[${index}].trade_available_on`} required />
      )}
      <Form.YesNo
        name={`aryTrade[${index}].has_sif`}
        disabled={!(!needToPay && ['NY', 'MD'].includes(titleState))}
        label="Do You Have SIF?"
      />
      <Form.YesNo name={`aryTrade[${index}].vehicle_registration`} label="Vehicle Registration?" required />
      <Attachment.Upload
        label="Vehicle Registration"
        name={`aryTrade[${index}].inventory.documentAttachment.trade_registration`}
        uploadType="inventory"
        documentType={INVENTORY_FILE_TYPE_IDS.TRADE_REGISTRATION}
        docrName={`aryTrade[${index}].inventory.docrAttachmentRequest`}
        uploadParams={uploadParams}
        requestedFor="Inventory"
        vehicle={vehicle}
      />
    </Form.Body>
  );
}
